import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BRAND,
  fmtDate,
  getReport,
  metricCurR,
  money,
  quoteTotal,
  rColor,
  reportFromInspection,
} from './reportData';
import { BEST_TIMES, requestBooking } from '../../services/bookingRequests';
import { CATEGORY_ORDER } from '../../services/catalog';
import './ReportView.css';

/**
 * Customer inspection report — a 1:1 port of the live CIE report
 * (canadianinsulationexperts.com/r/<token>, built by report-builder.html → buildReport).
 * It always renders as the 412px phone card on the grey backdrop, desktop included.
 *
 * Data, in order of preference:
 *   report                   a report object ({ brand, header, sections, quotes })
 *   token                    a report link token (/r/<token>)
 *   inspection + packageData the inspection + quote flow
 *
 * variant="agent"    in-app preview: working back button + LIVE pill, like the builder's preview
 * variant="customer" the shared link: no agent controls, like the published page
 */
/* On the customer's own phone the request form doesn't reach the office yet — they call or text us instead */
const BOOK_TEXT = (quote) => `Hi, I'd like to book${quote ? ` the ${quote.name} package` : ' my job'}.`;
const ContactSheet = ({ quote, onClose }) => (
  <div className="book-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="book-sheet" role="dialog" aria-label="Book your job">
      <div className="book-grab" />
      <div className="book-done">
        <h3>Book your job</h3>
        <p>
          {quote ? `${quote.name} · ${money(quoteTotal(quote))}. ` : ''}Call or text us and we’ll pick a date with you.
        </p>
        <a className="book-submit book-link" href="tel:+14169904555">📞 Call 416-990-4555</a>
        <a className="book-submit book-link book-alt" href={`sms:+12497004555?&body=${encodeURIComponent(BOOK_TEXT(quote))}`}>💬 Text us</a>
        <button type="button" className="book-close-text" onClick={onClose}>Not now</button>
      </div>
    </div>
  </div>
);

/* "Book your job now" — the customer confirms their details and asks the office to call (no new page) */
const BookingSheet = ({ data, quote, onClose }) => {
  const h = data.header;
  const [form, setForm] = useState({
    name: h.customer || '',
    phone: (data.contact && data.contact.phone) || '',
    email: (data.contact && data.contact.email) || '',
    address: [h.address, h.cityline].filter(Boolean).join(', '),
    bestTime: '',
    note: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setSending(true);
    setError('');
    try {
      await requestBooking({ ...form, leadId: data.leadId, quote: quote && { name: quote.name, total: money(quoteTotal(quote)) } });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong — please call us.');
    }
    setSending(false);
  };

  return (
    <div className="book-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="book-sheet" role="dialog" aria-label="Book your job">
        <div className="book-grab" />
        {done ? (
          <div className="book-done">
            <div className="book-done-icon">✓</div>
            <h3>Request sent</h3>
            <p>Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}! Our office will call you{form.bestTime ? ` in the ${form.bestTime.toLowerCase()}` : ''} to book your job.</p>
            <button type="button" className="book-submit" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="book-head">
              <div>
                <h3>Book your job</h3>
                <p>Confirm your details and we’ll call you to pick a date.</p>
              </div>
              <button type="button" className="book-close" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="book-body">
              {quote && (
                <div className="book-package">
                  <span>{quote.name}</span>
                  <b>{money(quoteTotal(quote))}</b>
                </div>
              )}

              <label className="book-label" htmlFor="book-name">Name</label>
              <input id="book-name" className="book-input" value={form.name} onChange={set('name')} autoComplete="name" />

              <label className="book-label" htmlFor="book-phone">Phone</label>
              <input id="book-phone" className="book-input" type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" />

              <label className="book-label" htmlFor="book-email">Email <span>optional</span></label>
              <input id="book-email" className="book-input" type="email" value={form.email} onChange={set('email')} autoComplete="email" />

              <label className="book-label" htmlFor="book-address">Address</label>
              <input id="book-address" className="book-input" value={form.address} onChange={set('address')} autoComplete="street-address" />

              <div className="book-label">Best time to call <span>optional</span></div>
              <div className="book-times">
                {BEST_TIMES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`book-time${form.bestTime === t ? ' on' : ''}`}
                    onClick={() => setForm({ ...form, bestTime: form.bestTime === t ? '' : t })}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label className="book-label" htmlFor="book-note">Anything we should know? <span>optional</span></label>
              <textarea id="book-note" className="book-input" rows={2} value={form.note} onChange={set('note')} />

              {error && <p className="book-error">{error}</p>}
            </div>

            <div className="book-foot">
              <button type="button" className="book-submit" disabled={sending} onClick={submit}>
                {sending ? 'Sending…' : '📞 Request a call to book'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// service summary: a quote's items under their category (catalog order), with the customer's wording
const CATEGORY_LABEL = { Others: 'Other Services', 'Enbridge Authorized Inspector': 'Energy Audit' };
const byCategory = (lines) => {
  const groups = new Map();
  lines.forEach((l) => {
    const c = l.category || '';
    if (!groups.has(c)) groups.set(c, []);
    groups.get(c).push(l);
  });
  const rank = (c) => (CATEGORY_ORDER.indexOf(c) === -1 ? CATEGORY_ORDER.length : CATEGORY_ORDER.indexOf(c));
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([category, items]) => ({ category, label: CATEGORY_LABEL[category] || category, items }));
};

const ReportView = ({ report, token, inspection, packageData, onBack, variant = 'agent' }) => {
  const data = report || (token !== undefined ? getReport(token) : reportFromInspection(inspection, packageData));

  const [openQuotes, setOpenQuotes] = useState({});
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const [chosen, setChosen] = useState(null); // the package the customer tapped
  const [booking, setBooking] = useState(false);

  const stepLightbox = useCallback(
    (dir) => setLightbox((lb) => lb && { ...lb, index: (lb.index + dir + lb.photos.length) % lb.photos.length }),
    []
  );

  const lightboxOpen = !!lightbox;
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
      else if (e.key === 'ArrowRight') stepLightbox(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, stepLightbox]);

  // the customer's tab reads like the published page: "Canadian Insulation Experts — 121 Baycliffe Dr"
  useEffect(() => {
    if (variant !== 'customer') return undefined;
    const previous = document.title;
    const who = data ? data.header.address || data.header.customer : '';
    document.title = data ? `${data.brand.company} — ${who || 'Energy Report'}` : `Your Energy Report — ${BRAND.company}`;
    return () => {
      document.title = previous;
    };
  }, [variant, data]);

  // reports carry names, home addresses and prices — never indexed, never leak a referrer (as the live viewer)
  useEffect(() => {
    if (variant !== 'customer') return undefined;
    const tags = [
      ['robots', 'noindex, nofollow, noarchive, nosnippet, noimageindex'],
      ['referrer', 'no-referrer'],
    ].map(([name, content]) => {
      const tag = document.createElement('meta');
      tag.name = name;
      tag.content = content;
      document.head.appendChild(tag);
      return tag;
    });
    return () => tags.forEach((tag) => tag.remove());
  }, [variant]);

  // a link that points nowhere gets the same card the live /r/ viewer shows
  if (!data) {
    const missingToken = !token;
    return createPortal(
      <div className={`cie-report cie-report--${variant}`}>
        <div className="panel">
          <div className="card">
            <div className="mark">CANADIAN <i>INSULATION</i> EXPERTS</div>
            <h1>{missingToken ? 'Report not found' : 'This link isn’t available'}</h1>
            <p>
              {missingToken
                ? 'This link doesn’t point to a report. Check the link in your email, or give us a call and we’ll resend it.'
                : 'It may have expired, or the report may have been replaced by a newer version. Give us a call and we’ll send you the current one.'}
            </p>
            <a className="call" href="tel:+14169904555">📞 (416) 990-4555</a>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const b = data.brand;
  const defaultIdx = Math.max(0, data.quotes.findIndex((q) => q.selected));
  const chosenIdx = chosen ?? defaultIdx;
  const chosenQuote = data.quotes[chosenIdx];
  const h = data.header;
  const accent = b.accent || BRAND.accent;

  // a real tel: link — it has to work on the customer's phone and still read as a number on a PDF
  let telDigits = String(b.phone || '').replace(/[^\d+]/g, '');
  if (/^\d{10}$/.test(telDigits)) telDigits = `+1${telDigits}`;

  const renderMetrics = (m) => {
    const tgt = Number(m.tgtR) || 60;
    const depthN = parseFloat(m.depth) || 0;
    // no measured depth yet → the current R-value and progress stay empty placeholders
    if (!depthN) {
      return (
        <div className="mcard">
          <div className="g3">
            <div className="c"><small>Measured Depth</small><b>—</b></div>
            <div className="c"><small>Material</small><b>{m.material || '—'}</b></div>
            <div className="c"><small>Target Standard</small><b>R-{tgt}</b></div>
          </div>
          <div className="rr">
            <div className="tops">
              <div className="cur"><small>Current</small><b style={{ color: 'var(--muted)' }}>—</b></div>
              <div className="tgt"><small>Target</small><b>R-{tgt}</b></div>
            </div>
            <div className="track" />
            <div className="foot"><span className="l" /><span className="r">R-0 → R-{tgt}</span></div>
          </div>
        </div>
      );
    }
    const cur = metricCurR(m);
    const pct = tgt > 0 ? Math.round((cur / tgt) * 100) : 0;
    const col = rColor(cur, tgt);
    return (
      <div className="mcard">
        <div className="g3">
          <div className="c"><small>Measured Depth</small><b>{depthN ? `${depthN}"` : '—'}</b></div>
          <div className="c"><small>Material</small><b>{m.material || '—'}</b></div>
          <div className="c"><small>Target Standard</small><b>R-{tgt}</b></div>
        </div>
        <div className="rr">
          <div className="tops">
            <div className="cur"><small>Current</small><b style={{ color: col }}>R-{cur}</b></div>
            <div className="tgt"><small>Target</small><b>R-{tgt}</b></div>
          </div>
          <div className="track"><div className="fill" style={{ width: `${Math.max(pct, 4)}%`, background: col }} /></div>
          <div className="foot">
            <span className="l" style={{ color: col }}>You’re at {pct}% of target</span>
            <span className="r">R-0 → R-{tgt}</span>
          </div>
        </div>
      </div>
    );
  };

  const lbPhotos = lightbox ? lightbox.photos : [];
  const lbMulti = lbPhotos.length > 1;

  return createPortal(
    <div className={`cie-report cie-report--${variant}${lightbox ? ' lb-open' : ''}`}>
      <div className="share-wrap">
        <div className="device">
          {/* top bar */}
          <div className="rtop">
            <button type="button" className="back" onClick={onBack} aria-label="Back">←</button>
            <div className="ttl"><small>Inspection Report</small><b>{h.customer}</b></div>
            <div className={`live${b.live ? '' : ' off'}`}><span className="dot" />LIVE</div>
          </div>

          {/* hero */}
          <div className="hero">
            {h.hero && <img src={h.hero} alt="" />}
            <div className="veil" />
            <div className="in">
              <div className="brandrow">
                <div className="co">
                  <div>
                    <div className="nm">{(b.brandName || b.company || '').toUpperCase()}</div>
                    <div className="tg">{b.tagline}</div>
                  </div>
                </div>
                <div className="rep">Inspection Report</div>
              </div>
              <div className="spacer" />
              <div className="eyebrow"><span className="dot" />{(b.reportType || '').toUpperCase()}</div>
              <h1>{h.address}</h1>
              <div className="addr">{h.cityline}</div>
              <div className="date">
                📅 {fmtDate(h.date)}{b.preparer ? `  •  Prepared by ${b.preparer}` : ''}
              </div>
            </div>
          </div>

          <div className="rbody">
            {/* findings */}
            {data.sections.map((s) => {
              const photos = s.photos || [];
              const bullets = (s.bullets || []).filter((x) => x && x.trim());
              return (
                <div className="rcard" key={s.id} style={{ '--sec': s.accent || accent }}>
                  {/* no photos yet → keep the empty photo slot so the template shows where they go */}
                  {photos.length === 0 && <div className="ph" />}
                  {photos.length > 0 && (
                    <div className="ph">
                      <div className="gal">
                        {photos.map((src, pi) => (
                          <img key={pi} src={src} alt="" onClick={() => setLightbox({ photos, index: pi })} />
                        ))}
                      </div>
                      {photos.length > 1 && (
                        <span className="count"><span className="d" />{photos.length} photos · swipe</span>
                      )}
                    </div>
                  )}
                  <div className="bd">
                    <div className="loc">📍 {s.tag}</div>
                    <h3>{s.title}</h3>
                    <ul>
                      {bullets.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                    {s.metrics && s.metrics.on && renderMetrics(s.metrics)}
                  </div>
                </div>
              );
            })}

            {/* choose your fix — one card per package built in the quote builder */}
            {data.quotes.length === 0 && data.template && (
              <div className="fix">
                <h2>Choose your fix</h2>
                <div className="sub">Workmanship-warrantied · tap a quote to see the breakdown.</div>
                <div className="quote quote-placeholder">The packages you build for this customer show here.</div>
              </div>
            )}
            {data.quotes.length > 0 && (
              <div className="fix">
                <h2>Choose your fix</h2>
                <div className="sub">Workmanship-warrantied · tap a quote to see the breakdown.</div>
                {data.quotes.map((q, qi) => {
                  // customer-facing breakdown: scope of work only — no per-line cost, sq ft, or $/ft²
                  const lines = (q.lines || []).filter((l) => l.on && l.label && l.label.trim());
                  const open = !!openQuotes[qi];
                  return (
                    <div key={q.id ?? qi} className={`quote${qi === chosenIdx ? ' sel' : ''}${open ? ' open' : ''}`}>
                      {/* tap a package to choose it — "Book your job now" books the chosen one */}
                      <div className="top pick" role="button" tabIndex={0} onClick={() => setChosen(qi)} onKeyDown={(e) => e.key === 'Enter' && setChosen(qi)}>
                        <span className="chk">{qi === chosenIdx ? '✓' : ''}</span>
                        <span className="nm"><b>{q.name}</b><small>{q.subtitle}</small></span>
                        <span className="price">{money(quoteTotal(q))}</span>
                      </div>
                      {lines.length > 0 && (
                        <>
                          <button
                            type="button"
                            className="more"
                            aria-expanded={open}
                            onClick={() => setOpenQuotes((o) => ({ ...o, [qi]: !o[qi] }))}
                          >
                            View service summary <span>›</span>
                          </button>
                          <div className="brk">
                            {/* grouped under category bars (older reports have no categories → one plain list) */}
                            {byCategory(lines).map((g) => (
                              <div className="cat" key={g.category || 'all'}>
                                {g.label && <div className="cat-bar">{g.label}</div>}
                                {g.items.map((l, li) => (
                                  <div className="li" key={l.id ?? li}>
                                    <span className="li-txt">
                                      <b>{l.label}</b>
                                      {l.desc && <small>{l.desc}</small>}
                                    </span>
                                    {l.amount != null && <span className="li-amt">{Number(l.amount) ? money(l.amount) : 'Included'}</span>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* company card */}
            <div className="cocard">
              <div className="r1">
                <span className="av">{b.initials}</span>
                <div className="who">
                  <b>{b.company}</b>
                  <small>{b.preparer ? `Prepared by ${b.preparer} · ` : ''}Certified Technician</small>
                </div>
                {b.phone && <a className="call" href={`tel:${telDigits}`}>📞 {b.phone}</a>}
              </div>
              <div className="stats">
                <div className="s"><b>{b.s1b}</b><small>{b.s1l}</small></div>
                <div className="s"><b>{b.s2b}</b><small>{b.s2l}</small></div>
                <div className="s"><b>{b.s3b}</b><small>{b.s3l}</small></div>
              </div>
              {(b.email || b.website) && (
                <div className="reach">
                  {b.email && <span>✉ {b.email}</span>}
                  {b.website && <span>🌐 {b.website}</span>}
                </div>
              )}
            </div>
          </div>

          {/* stays at the bottom of the phone while the customer reads */}
          {/* nothing to book until the package has a price */}
          {chosenQuote && quoteTotal(chosenQuote) > 0 && (
            <div className="book-bar">
              <button type="button" className="book-btn" onClick={() => setBooking(true)}>
                Book your job now · {money(quoteTotal(chosenQuote))} <span aria-hidden="true">→</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {booking &&
        (variant === 'customer' ? (
          <ContactSheet quote={chosenQuote} onClose={() => setBooking(false)} />
        ) : (
          <BookingSheet data={data} quote={chosenQuote} onClose={() => setBooking(false)} />
        ))}

      {/* tap a photo → full screen, swipe/arrow through that finding's gallery */}
      {lightbox && (
        <div className="lb-overlay on" onClick={(e) => e.target === e.currentTarget && setLightbox(null)}>
          <button type="button" className="lb-close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
          {lbMulti && (
            <button type="button" className="lb-nav lb-prev" aria-label="Previous" onClick={() => stepLightbox(-1)}>‹</button>
          )}
          <img className="lb-img" alt="" src={lbPhotos[lightbox.index]} />
          {lbMulti && (
            <button type="button" className="lb-nav lb-next" aria-label="Next" onClick={() => stepLightbox(1)}>›</button>
          )}
          {lbMulti && <div className="lb-count">{lightbox.index + 1} / {lbPhotos.length}</div>}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ReportView;
